import { Injectable } from "@angular/core";
import { getAuth } from "firebase/auth";
import { BehaviorSubject } from "rxjs";
import {
  SalesJourneyIdentity,
  SalesJourneyIdentityEnvelopeFields,
  SalesJourneyIdentityOptions,
  SalesJourneyIdentityPayload,
} from "../interfaces/sales-journey-identity.i";

@Injectable({
  providedIn: "root",
})
export class SalesJourneyIdentityService {
  callSigUpForSalesResponse$ = new BehaviorSubject(null);

  /**
   * Bumped on every reset so late signup responses from a previous journey
   * cannot repopulate the registration identity cache after logout/switch.
   */
  private registrationIdentityEpoch = 0;

  /**
   * Resolves a consistent sales-journey identity snapshot.
   * Repeat (authenticated) users are DFCI-first; anonymous users are anonymous-first.
   * SQL `customer_id` is never inferred from DFCI/anonymous UID.
   */
  getSalesJourneyIdentity(options: SalesJourneyIdentityOptions = {}): SalesJourneyIdentity {
    const auth = getAuth();
    const currentUser = auth?.currentUser ?? null;
    const isAnonymous = !!currentUser?.isAnonymous;

    const dfciUserId = this.resolveDfciUserId(options, currentUser?.uid, isAnonymous);
    const anonymousId = this.resolveAnonymousId(options, currentUser?.uid, isAnonymous);
    const customerId = this.resolveSqlCustomerId(options);

    return {
      anonymousId,
      userId: dfciUserId,
      customerId,
      msn: this.normalizeMsn(options.msn),
      wpUserId: this.resolveWpUserId(options),
      journeyId: this.resolveJourneyId(options),
      checkoutAttemptId: this.resolveCheckoutAttemptId(options),
    };
  }

  toIdentityPayload(identity: SalesJourneyIdentity): SalesJourneyIdentityPayload {
    return {
      anonymous_id: identity.anonymousId,
      user_id: identity.userId,
      customer_id: identity.customerId,
      msn: identity.msn,
      wp_user_id: identity.wpUserId,
      journey_id: identity.journeyId,
      checkout_attempt_id: identity.checkoutAttemptId,
    };
  }

  /**
   * Applies a nested `identity` envelope for backend transition.
   */
  applyIdentityEnvelope<T extends Record<string, unknown>>(
    target: T,
    options: SalesJourneyIdentityOptions = {},
  ): T & SalesJourneyIdentityEnvelopeFields {
    const identity = this.getSalesJourneyIdentity(options);
    const payload = this.toIdentityPayload(identity);

    return {
      ...target,
      identity: payload,
    };
  }

  /** Clears cached registration identity and invalidates in-flight signup responses. */
  resetRegistrationIdentity(): void {
    this.registrationIdentityEpoch += 1;
    this.callSigUpForSalesResponse$.next(null);
  }

  /**
   * Returns the existing sales `journeyId`, or creates and stores one once.
   * Does not replace an in-flight journey ID (payment re-init / refresh safe).
   * Distinct from `checkout_attempt_id` (`myuuid`).
   */
  ensureJourneyId(): string {
    try {
      const existing = localStorage.getItem("journeyId");
      if (existing) {
        return existing;
      }
    } catch {
      // Fall through and create a new ID when storage read fails.
    }
    const journeyId = Date.now().toString();
    try {
      localStorage.setItem("journeyId", journeyId);
    } catch {
      // Still return the in-memory value so callers can attach it to requests.
    }
    return journeyId;
  }

  /** Clears `journeyId` so the next ensure/start creates a new journey. */
  resetJourneyId(): void {
    try {
      localStorage.removeItem("journeyId");
    } catch {
      // Ignore storage failures on reset.
    }
  }

  /**
   * Explicit new-journey boundary: clear any prior `journeyId`, then create one.
   * Call at registration entry only — not on payment page re-init.
   */
  startNewSalesJourney(): string {
    this.resetRegistrationIdentity();
    this.resetJourneyId();
    return this.ensureJourneyId();
  }

  /** Snapshot for callers to stamp in-flight signup requests. */
  captureRegistrationIdentityEpoch(): number {
    return this.registrationIdentityEpoch;
  }

  /**
   * Stores a signup response only when it still belongs to the current journey epoch.
   * Prevents a delayed response from a prior logout/registration from repopulating the cache.
   * @returns true when the response was stored for the current epoch.
   */
  setRegistrationIdentity(response: unknown, epoch: number): boolean {
    if (epoch !== this.registrationIdentityEpoch) {
      return false;
    }
    this.callSigUpForSalesResponse$.next(response);
    return true;
  }

  private resolveDfciUserId(
    options: SalesJourneyIdentityOptions,
    currentUid: string | undefined,
    isAnonymous: boolean,
  ): string | null {
    if (options.userId) {
      return options.userId;
    }
    if (isAnonymous) {
      return null;
    }
    return currentUid ?? null;
  }

  private resolveAnonymousId(
    options: SalesJourneyIdentityOptions,
    currentUid: string | undefined,
    isAnonymous: boolean,
  ): string | null {
    if (!isAnonymous && !options.anonymousId) {
      return null;
    }
    if (options.anonymousId) {
      return options.anonymousId;
    }
    if (isAnonymous && currentUid) {
      return currentUid;
    }
    return this.getStoredAnonymousId();
  }

  /**
   * SQL customer_id only — never DFCI/anonymous UID.
   * Returns null when the real SQL identity is unknown.
   */
  private resolveSqlCustomerId(options: SalesJourneyIdentityOptions): string | null {
    const customerId =
      options.customerId ?? this.callSigUpForSalesResponse$.value?.response?.customer_id;

    return customerId == null || customerId === "" ? null : String(customerId);
  }

  private resolveWpUserId(options: SalesJourneyIdentityOptions): number | string | null {
    if (options.wpUserId != null && options.wpUserId !== "") {
      return options.wpUserId;
    }
    try {
      const stored = localStorage.getItem("wp_user_id");
      if (!stored) {
        return null;
      }
      const parsed = parseInt(stored, 10);
      return Number.isNaN(parsed) ? stored : parsed;
    } catch {
      return null;
    }
  }

  private resolveJourneyId(options: SalesJourneyIdentityOptions): string | number | null {
    if (options.journeyId != null && options.journeyId !== "") {
      return options.journeyId;
    }
    try {
      const stored = localStorage.getItem("journeyId");
      if (!stored) {
        return null;
      }
      const parsed = parseInt(stored, 10);
      return Number.isNaN(parsed) ? stored : parsed;
    } catch {
      return null;
    }
  }

  private resolveCheckoutAttemptId(options: SalesJourneyIdentityOptions): string | null {
    if (options.checkoutAttemptId) {
      return options.checkoutAttemptId;
    }
    try {
      return localStorage.getItem("myuuid") || null;
    } catch {
      return null;
    }
  }

  private getStoredAnonymousId(): string | null {
    try {
      return localStorage.getItem("anonymousId") || localStorage.getItem("anonymous_id") || null;
    } catch {
      return null;
    }
  }

  private normalizeMsn(msn: string | null | undefined): string | null {
    if (msn == null || msn === "") {
      return null;
    }
    return String(msn);
  }
}
